import { createSwiper } from '@nulo/swiper';

/**
 * @extends {HTMLElement}
 */
class NuloAmbassadorGrid extends HTMLElement {
  /** @type {number} */
  #itemsPerPage = 9;

  /** @type {number} */
  #currentPage = 0;

  /** @type {string} */
  #filter = 'Current';

  /** @type {'grid' | 'related'} */
  #displayMode = 'grid';

  /** @type {string} */
  #articleHandle = '';

  /** @type {HTMLElement[]} */
  #cards = [];

  /** @type {NodeListOf<HTMLButtonElement> | undefined} */
  #filterBtns;

  /** @type {HTMLElement | null} */
  #loadMoreWrap = null;

  /** @type {HTMLElement | null} */
  #viewAllWrap = null;

  /** @type {HTMLElement | null} */
  #carousel = null;

  /** @type {import('swiper').Swiper | null} */
  #swiper = null;

  /** @type {boolean} */
  #initialized = false;

  connectedCallback() {
    // Defer so child cards exist when the element upgrades mid-parse.
    queueMicrotask(() => this.#init());
  }

  disconnectedCallback() {
    this.#destroySwiper();
    this.#initialized = false;
    this.dataset.bound = 'false';
  }

  #init() {
    if (this.#initialized || !this.isConnected) return;
    this.#initialized = true;
    this.dataset.bound = 'true';

    this.#itemsPerPage = parseInt(this.dataset.itemsPerPage || '9', 10);
    this.#displayMode = this.dataset.displayMode === 'related' ? 'related' : 'grid';
    this.#articleHandle = this.#normalizeHandle(this.dataset.articleHandle || '');
    this.#filterBtns = this.querySelectorAll('[data-filter-btn]');
    this.#cards = Array.from(this.querySelectorAll('[data-ambassador-card]'));
    this.#loadMoreWrap = this.querySelector('[data-load-more-wrap]');
    this.#viewAllWrap = this.querySelector('[data-view-all-wrap]');
    this.#carousel = this.querySelector('[data-related-carousel]');
    const loadMoreBtn = this.querySelector('[data-load-more-btn]');

    if (this.#displayMode === 'related') {
      this.#renderRelated();
      return;
    }

    for (const btn of this.#filterBtns ?? []) {
      btn.addEventListener('click', () => {
        this.#filter = btn.dataset.filterBtn || 'Current';
        this.#currentPage = 0;
        this.#updateFilterUI();
        this.#render();
      });
    }

    loadMoreBtn?.addEventListener('click', () => {
      this.#currentPage++;
      this.#render();
    });

    this.#render();
  }

  #updateFilterUI() {
    if (!this.#filterBtns) return;

    for (const btn of this.#filterBtns) {
      const active = btn.dataset.filterBtn === this.#filter;
      btn.classList.toggle('ambassador-filter-btn--inactive', !active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
  }

  #filteredCards() {
    let cards = [...this.#cards];

    if (this.#filter === 'Current') {
      cards = cards.filter((card) => card.dataset.ambassadorType === 'Current');
    } else if (this.#filter === 'All Time') {
      cards = cards.sort((a, b) =>
        (a.dataset.sortKey || '').localeCompare(b.dataset.sortKey || ''),
      );
    }

    return cards;
  }

  #render() {
    const filtered = this.#filteredCards();
    const visible = (this.#currentPage + 1) * this.#itemsPerPage;

    for (const card of this.#cards) {
      card.setAttribute('hidden', '');
    }

    for (const card of filtered.slice(0, visible)) {
      card.removeAttribute('hidden');
    }

    if (!this.#loadMoreWrap) return;

    if (visible >= filtered.length) {
      this.#loadMoreWrap.setAttribute('hidden', '');
    } else {
      this.#loadMoreWrap.removeAttribute('hidden');
    }
  }

  /**
   * Oxygen SectionAmbassadorCards related slice:
   * same type only, exclude current, up to 3 cards.
   * first → next 3; last → prev 3; second → 1 prev + 2 next; else → 2 prev + 1 next.
   */
  #renderRelated() {
    const related = this.#getRelatedCards();

    for (const card of this.#cards) {
      card.setAttribute('hidden', '');
    }

    // Only hide the section when there are truly no cards to show
    // (e.g. current ambassador is alone in its type).
    if (related.length === 0) {
      this.setAttribute('hidden', '');
      return;
    }

    this.removeAttribute('hidden');

    for (const card of related) {
      card.removeAttribute('hidden');
    }

    this.#viewAllWrap?.removeAttribute('hidden');
    this.#initRelatedCarousel(related);
  }

  /** @returns {HTMLElement[]} */
  #getRelatedCards() {
    if (this.#cards.length === 0) return [];

    const currentHandle = this.#resolveArticleHandle();
    if (!currentHandle) {
      // No article context — show first 3 so the section isn't blank in editor.
      return this.#cards.slice(0, 3);
    }

    const sameTypePool = this.#cards.filter((card) => {
      const handle = this.#cardHandle(card);
      return handle === currentHandle;
    });

    const currentCard = sameTypePool[0] ?? null;
    if (!currentCard) {
      // Article not in the block list — still show 3 cards rather than a blank section.
      return this.#cards.slice(0, 3);
    }

    const currentType = currentCard.dataset.ambassadorType || '';
    const sameType = this.#cards.filter(
      (card) => (card.dataset.ambassadorType || '') === currentType,
    );
    const sameTypeIndex = sameType.findIndex(
      (card) => this.#cardHandle(card) === currentHandle,
    );

    if (sameTypeIndex === -1 || sameType.length <= 1) return [];

    const total = sameType.length;
    /** @type {HTMLElement[]} */
    let related = [];

    if (sameTypeIndex === 0) {
      related = sameType.slice(1, 4);
    } else if (sameTypeIndex === total - 1) {
      related = sameType.slice(Math.max(0, sameTypeIndex - 3), sameTypeIndex);
    } else if (sameTypeIndex === 1) {
      related = [
        sameType[0],
        ...sameType.slice(sameTypeIndex + 1, sameTypeIndex + 3),
      ];
    } else {
      const previous = sameType.slice(sameTypeIndex - 2, sameTypeIndex);
      const next =
        sameTypeIndex + 1 < total ? [sameType[sameTypeIndex + 1]] : [];
      related = [...previous, ...next];
    }

    return related.slice(0, 3);
  }

  /** @returns {string} */
  #resolveArticleHandle() {
    if (this.#articleHandle) return this.#articleHandle;
    return this.#normalizeHandle(window.location.pathname);
  }

  /**
   * @param {HTMLElement} card
   * @returns {string}
   */
  #cardHandle(card) {
    const fromData = this.#normalizeHandle(card.dataset.ambassadorHandle || '');
    if (fromData) return fromData;

    const link = card.querySelector('a[href]');
    if (!(link instanceof HTMLAnchorElement)) return '';

    const href = link.getAttribute('href') || link.pathname || '';
    return this.#normalizeHandle(href);
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #normalizeHandle(value) {
    if (!value) return '';

    let path = value.trim();
    try {
      if (path.includes('://')) {
        path = new URL(path).pathname;
      }
    } catch {
      // keep raw value
    }

    const parts = path.split('/').filter(Boolean);
    const last = parts[parts.length - 1] || '';
    try {
      return decodeURIComponent(last.split('?')[0].split('#')[0]).toLowerCase();
    } catch {
      return last.split('?')[0].split('#')[0].toLowerCase();
    }
  }

  /**
   * @param {HTMLElement[]} related
   */
  #initRelatedCarousel(related) {
    this.#destroySwiper();

    if (!(this.#carousel instanceof HTMLElement) || related.length === 0) return;

    const wrapper = this.#carousel.querySelector('[data-related-swiper-wrapper]');
    const swiperEl = this.#carousel.querySelector('[data-swiper]');
    if (!(wrapper instanceof HTMLElement) || !(swiperEl instanceof HTMLElement)) {
      return;
    }

    wrapper.replaceChildren();

    for (const card of related) {
      const slide = document.createElement('div');
      slide.className = 'swiper-slide ambassador-grid-section__slide';
      slide.appendChild(card.cloneNode(true));
      const clonedCard = slide.querySelector('[data-ambassador-card]');
      clonedCard?.removeAttribute('hidden');
      wrapper.appendChild(slide);
    }

    this.#carousel.removeAttribute('hidden');

    const prevEl = this.#carousel.querySelector('[data-related-prev]');
    const nextEl = this.#carousel.querySelector('[data-related-next]');
    const showNavigation = related.length > 1 && prevEl && nextEl;

    /** @type {Record<string, unknown>} */
    const config = {
      speed: 500,
      slidesPerView: 1.5,
      slidesPerGroup: 1,
      spaceBetween: 16,
      watchOverflow: true,
      watchSlidesProgress: true,
      breakpoints: {
        480: {
          slidesPerView: 1.5,
          spaceBetween: 16,
        },
        768: {
          slidesPerView: 1.8,
          spaceBetween: 16,
        },
      },
    };

    if (showNavigation) {
      config.navigation = { nextEl, prevEl };
    }

    this.#swiper = createSwiper({
      region: this.#carousel,
      el: swiperEl,
      activeSlideClass: 'swiper-slide-fully-visible',
      config,
    });
  }

  #destroySwiper() {
    this.#swiper?.destroy?.(true, true);
    this.#swiper = null;
  }
}

if (!customElements.get('nulo-ambassador-grid')) {
  customElements.define('nulo-ambassador-grid', NuloAmbassadorGrid);
}
